import React, { useEffect, useState } from "react";
import Lottie from "react-lottie";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react";
import userDetail from "Stores/userDetail";
import { initialWelcomePopupState } from "Components/common/InitialWelcomePopup";
import CopilotChatbotLottie from "Utils/LottieFiles/copilotChatbot.json";
import { ReactComponent as ClaudeLogo } from "Assets/svg/claudeAiLogo.svg";
import { ReactComponent as OpenAiLogo } from "Assets/svg/chatgptLogo.svg";
import "./copilotIntro.scss";

/**
 * One-time Copilot introduction for users who land on the dashboard (users
 * with data — first-timers land on /copilot itself via DashboardRouting).
 *
 * Politeness rules: never stacks on another popup (the signup welcome /
 * onboarding flow wins — we simply don't show this session and try again on
 * the next login), and once dismissed or acted on it never returns.
 */

const triedKey = () =>
  `sg_copilot_intro_tried_${userDetail?.userInfo?.memberId || "anon"}`;
const snoozeKey = () =>
  `sg_copilot_intro_snooze_${userDetail?.userInfo?.memberId || "anon"}`;
const bannerKey = () =>
  `sg_copilot_banner_dismissed_${userDetail?.userInfo?.memberId || "anon"}`;

const today = () => new Date().toISOString().slice(0, 10);

/** Copilot was actually used — the intro popup retires permanently. Also
 *  called when the user reaches /copilot organically, so we never nag
 *  someone who found it on their own. */
export const markCopilotTried = () => {
  try {
    localStorage.setItem(triedKey(), "1");
  } catch (e) {
    /* best-effort */
  }
};

const markSeen = key => {
  try {
    localStorage.setItem(key(), "1");
  } catch (e) {
    /* storage unavailable — worst case the intro shows again */
  }
};
const isSeen = key => {
  try {
    return localStorage.getItem(key()) === "1";
  } catch (e) {
    return true;
  }
};

const LOTTIE_OPTIONS = {
  loop: true,
  autoplay: true,
  animationData: CopilotChatbotLottie,
  rendererSettings: { preserveAspectRatio: "xMidYMid slice" }
};

// Product demo recording (drop the file at public/videos/copilot-intro.mp4).
// Until it exists — or if it fails to load — the Lottie stands in.
const INTRO_VIDEO_SRC = "/videos/copilot-intro.mp4";

export const CopilotIntroPopup = observer(() => {
  const history = useHistory();
  const [show, setShow] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    // Retired once Copilot has actually been tried; otherwise snoozed until
    // tomorrow each time the user picks "Maybe later".
    if (isSeen(triedKey)) return undefined;
    try {
      if (localStorage.getItem(snoozeKey()) === today()) return undefined;
    } catch (e) {
      /* storage unavailable */
    }
    // Give the signup welcome/onboarding popups first claim on the screen;
    // if one is up, skip silently WITHOUT snoozing so we retry next login.
    const timer = setTimeout(() => {
      const anotherPopupOpen =
        initialWelcomePopupState.showPopup ||
        initialWelcomePopupState.restrictOnboardingPopup ||
        userDetail.showUpgradePopup ||
        userDetail.showUpgradeSettingsPopup;
      if (!anotherPopupOpen) {
        setShow(true);
      }
    }, 1400);
    return () => clearTimeout(timer);
  }, []);

  if (!show) return null;

  // "Maybe later" only snoozes for today — the popup returns daily until
  // Copilot is tried once.
  const later = () => {
    try {
      localStorage.setItem(snoozeKey(), today());
    } catch (e) {
      /* best-effort */
    }
    setShow(false);
  };
  const tryCopilot = () => {
    markCopilotTried();
    setShow(false);
    history.push("/copilot");
  };

  return (
    <div className="copilotIntroPopup">
      <div className="copilotIntroPopup__overlay" />
      <div className="copilotIntroPopup__card">
        {videoFailed ? (
          <div className="copilotIntroPopup__lottie">
            <Lottie options={LOTTIE_OPTIONS} height={210} width={210} />
          </div>
        ) : (
          <div className="copilotIntroPopup__video">
            <video
              src={INTRO_VIDEO_SRC}
              autoPlay
              muted
              loop
              playsInline
              onError={() => setVideoFailed(true)}
            />
          </div>
        )}
        <div className="copilotIntroPopup__title">
          Introducing Salesgear Copilot
        </div>
        <div className="copilotIntroPopup__desc">
          The fastest way to build pipeline in the AI era. From prompt to
          pipeline in seconds. Just say who you want to target, Copilot builds
          the list, verifies the data, and launches the campaign.
        </div>
        <div className="copilotIntroPopup__features">
          <div className="copilotIntroPopup__feature">
            <span className="copilotIntroPopup__featureIcon">
              <i className="material-icons-outlined">search</i>
            </span>
            <span className="copilotIntroPopup__featureTitle">
              Find your buyers
            </span>
          </div>
          <div className="copilotIntroPopup__feature">
            <span className="copilotIntroPopup__featureIcon">
              <i className="material-icons-outlined">list_alt</i>
            </span>
            <span className="copilotIntroPopup__featureTitle">
              Build lists in seconds
            </span>
          </div>
          <div className="copilotIntroPopup__feature">
            <span className="copilotIntroPopup__featureIcon">
              <i className="material-icons-outlined">send</i>
            </span>
            <span className="copilotIntroPopup__featureTitle">
              Launch outreach
            </span>
          </div>
        </div>
        <button
          type="button"
          className="copilotIntroPopup__cta"
          onClick={tryCopilot}
        >
          Try Copilot now
        </button>
        <button
          type="button"
          className="copilotIntroPopup__later"
          onClick={later}
        >
          Maybe later
        </button>
        <div className="copilotIntroPopup__connectors">
          <span className="copilotIntroPopup__connectorsLabel">
            Now available in
          </span>
          <span className="copilotIntroPopup__connector">
            <ClaudeLogo /> Claude
          </span>
          <span className="copilotIntroPopup__connector">
            <OpenAiLogo /> ChatGPT
          </span>
        </div>
      </div>
    </div>
  );
});

/** Slim dashboard banner pointing at /copilot; dismissible, per member. */
export const CopilotIntroBanner = observer(() => {
  const history = useHistory();
  const [dismissed, setDismissed] = useState(() => isSeen(bannerKey));

  if (dismissed) return null;

  return (
    <div className="copilotIntroBanner">
      <span className="copilotIntroBanner__spark">
        <i className="material-icons-outlined">auto_awesome</i>
      </span>
      <span className="copilotIntroBanner__text">
        Build pipeline smarter and faster with <b>Salesgear Copilot</b>: find
        companies, build lists, and launch sequences by just asking.
      </span>
      <button
        type="button"
        className="copilotIntroBanner__cta"
        onClick={() => history.push("/copilot")}
      >
        Try Copilot
      </button>
      <button
        type="button"
        className="copilotIntroBanner__close"
        onClick={() => {
          markSeen(bannerKey);
          setDismissed(true);
        }}
      >
        <i className="material-icons-outlined">close</i>
      </button>
    </div>
  );
});
